import React, { useContext, useEffect, useState } from 'react';

import { toJS } from 'mobx';
import { MobXProviderContext, observer } from 'mobx-react';

import { Container, Popup, Table } from 'semantic-ui-react';

import {
  CONTENT_ITEM_TYPES,
  CourseListBanner,
  D3PieChart,
  DIALOG_NAMES,
  FeedbackIcon,
  NoAssignmentsMessage,
  ReportIdentityService,
  SatCoreComponent,
  SatCoreRegister,
  StudentAssignmentsPopup,
  VIEW_SELECTION,
  useParams
} from 'sat-core';

import '../css/ProgressViewOverride.less';

import emptyCardImage from '../img/empty-card.png';
import iconCommentActive from '../img/icon-comment-active.svg';

import WeeklyPerformanceChart from '../components/WeeklyPerformanceChart';

const Legend = ({ data }) => (
  <div aria-hidden={true} className='legend-container'>
    <div className='legend-item'>
      <div className='performance-badge advanced' />
      <div className='value advanced'>{data.advanced}</div>
      <div>(&gt; 75%)</div>
    </div>
    <div className='legend-item'>
      <div className='performance-badge competent' />
      <div className='value competent'>{data.competent}</div>
      <div>(50-75%)</div>
    </div>
    <div className='legend-item'>
      <div className='performance-badge beginner' />
      <div className='value beginner'>{data.beginner}</div>
      <div>(&lt; 50%)</div>
    </div>
  </div>
);

const PerformanceBadge = ({ value }) => {
  let performanceClass;
  if (value > 75) {
    performanceClass = 'advanced';
  } else if (value >= 50) {
    performanceClass = 'competent';
  } else {
    performanceClass = 'beginner';
  }

  return (
    <div aria-label={performanceClass} className={`performance-badge ${performanceClass}`} />
  );
};

const ProgressViewOverride = observer((props) => {
  const { history, t } = props;
  const {
    classroomManager,
    courseManager,
    dialogManager,
    navigationManager,
    studentProgressManager,
    userManager,
  } = useContext(MobXProviderContext);
  const { classroomProgressData, coursesVisibility, selectedContentType } = studentProgressManager;
  const { params: { view = VIEW_SELECTION.PROGRESS } } = useParams(props);

  const [renderAssignmentsPopup, setRenderAssignmentsPopup] = useState({
    activityInstanceId: '',
    courseElement: {},
    key: '',
    render: false
  });

  studentProgressManager.setClassroom(classroomManager.currentClassroomId);

  const KeyboardActionListener = SatCoreComponent('KeyboardActionListener');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => navigationManager.setView(view), []);

  useEffect(() => {
    async function fetchData() {
      const entityId = userManager.userId;
      const classroomId = classroomManager.currentClassroomId;
      await studentProgressManager.fetchStudentProgress(entityId, classroomId);
    }
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFeedBack = async (activityInstanceId) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.STUDENT_FEEDBACK, {
      assignmentInstanceId: activityInstanceId
    },
    () => dialogManager.closeDialog(DIALOG_NAMES.STUDENT_FEEDBACK));
  };

  const showAssignment = async (element, activityInstanceId) => {
    setRenderAssignmentsPopup({
      activityInstanceId,
      courseElement: element,
      key: Math.random(),
      render: true
    });
  };

  const hideAssignment = () => {
    setRenderAssignmentsPopup({
      activityInstanceId: '',
      courseElement: {},
      key: '',
      render: false
    });
  };

  const CourseElement = ({ element }) => {
    const contentString = ReportIdentityService.constructContentString('', element.elementName, element.elementDescription);
    const gradeData = classroomProgressData.grades[element.elementId];
    const feedbackData = classroomProgressData.feedback[element.elementId];
    let imageUrl = emptyCardImage;
    let gradeNum;
    let gradeStr = '';
    let elementClass;
    let clickable = false;

    switch (element.elementType) {
      case 'UNIT':
        elementClass = 'no-score-element';
        break;
      case 'SECTION':
        elementClass = 'no-score-element';
        break;
      default:
        gradeNum = (typeof gradeData.grade === 'number') && gradeData.grade;
        gradeStr = (typeof gradeData.grade === 'number') ? `${gradeData.grade}%` : 'n/a';
        clickable = true;
        break;
    }

    if (element.attachmentImageUrl) {
      imageUrl = CONTENT_ITEM_TYPES.getContentImageUrl(element.attachmentImageUrl);
    } else if (element.elementImageUrl) {
      imageUrl = CONTENT_ITEM_TYPES.getContentImageUrl(element.elementImageUrl);
    }

    const showAssignmentsClick = (clickable) ?
      () => showAssignment(element, gradeData.activityInstanceId) : null;

    return (
      <Table.Row className={elementClass} tabIndex={0}>
        <Table.Cell onClick={showAssignmentsClick} scope='col'>
          <div className='assignment-content'>
            <i aria-hidden='true' className='no-icon' />
            <img alt='' className='content-type-img' src={imageUrl} />
            <Popup
              content={<div className={`assignment-content-title ${clickable ? 'clickable' : ''}`}>{contentString}</div>}
              trigger={(
                <div
                  className={`assignment-content-title ${clickable ? 'clickable' : ''}`}
                  tabIndex={clickable ? 0 : undefined}>
                  {contentString}
                </div>
              )} />
          </div>
        </Table.Cell>
        <Table.Cell scope='col' textAlign='center'>{gradeStr}</Table.Cell>
        <Table.Cell scope='col' textAlign='center'>
          {typeof gradeNum === 'number' &&
            <PerformanceBadge value={gradeNum} />}
        </Table.Cell>
        <Table.Cell scope='col' textAlign='center'>
          {feedbackData && (
            <FeedbackIcon
              className='assignment-feedback'
              clickHandler={() => handleFeedBack(feedbackData.entityId)}
              hasFeedback={true}
              icon={iconCommentActive}
              tabIndex={0} />
          )}
        </Table.Cell>
      </Table.Row>
    );
  };

  const CourseElements = ({ elements }) => (
    elements.map((element) => (
      <CourseElement key={element.elementId} element={element} />
    ))
  );

  const NoAssignments = () => (
    <NoAssignmentsMessage message='No scores yet' />
  );

  const Assignments = () => (
    <>
      <Table unstackable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell scope='col'>Content</Table.HeaderCell>
            <Table.HeaderCell scope='col' textAlign='center'>Score</Table.HeaderCell>
            <Table.HeaderCell scope='col' textAlign='center'>Performance</Table.HeaderCell>
            <Table.HeaderCell scope='col' textAlign='center'>Feedback</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {hasAssignments && classroomProgressData.courses.map((course, i) => {
            const isExpandedRow = coursesVisibility[course.courseId];
            const dropdownIconClassName = isExpandedRow ? 'dropdown icon' : 'dropdown icon counterclockwise rotated';

            const imageUrl = (course.courseImageUrl) ?
              CONTENT_ITEM_TYPES.getContentImageUrl(course.courseImageUrl) : emptyCardImage;

            return (
              <React.Fragment key={course.courseId}>
                <Table.Row
                  aria-expanded={isExpandedRow}
                  className='no-score-element'
                  onClick={() => studentProgressManager.toggleCourseVisibility(course.courseId)}
                  tabIndex={0}>
                  <Table.Cell scope='col'>
                    <div
                      className='assignment-content'>
                      <i aria-hidden='true' className={dropdownIconClassName} />
                      <img alt='' className='content-type-img' src={imageUrl} />
                      <Popup
                        content={<div className='assignment-content-title'>{course.courseName}</div>}
                        trigger={(
                          <div className='assignment-content-title'>
                            {course.courseName}
                          </div>
                        )} />
                    </div>
                  </Table.Cell>
                  <Table.Cell scope='col' textAlign='center' />
                  <Table.Cell scope='col' textAlign='center' />
                  <Table.Cell scope='col' textAlign='center' />
                </Table.Row>
                {studentProgressManager.coursesVisibility[course.courseId] &&
                <CourseElements elements={course.courseElements} />}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
      {!hasAssignments && <NoAssignments />}
    </>
  );

  const { currentClassroomId } = classroomManager;
  const course = courseManager.getCurrentCourse(currentClassroomId);
  const gradeCount = Object.keys(classroomProgressData.grades).length;
  const hasAssignments = gradeCount > 0;
  const { summary } = classroomProgressData;
  const summaryPieData = [];
  let summaryHighCount, summaryMediumCount, summaryLowCount;
  if (summary) {
    summaryHighCount = summary.high || summary.advanced;
    if (summaryHighCount) {
      summaryPieData.push({
        color: '#009824',
        label: summaryHighCount.toString?.(),
        text: 'Advanced',
        value: summaryHighCount
      });
    }

    summaryMediumCount = summary.medium || summary.competent;
    if (summaryMediumCount) {
      summaryPieData.push({
        color: '#1b8af4',
        label: summaryMediumCount.toString?.(),
        text: 'Competent',
        value: summaryMediumCount
      });
    }

    summaryLowCount = summary.low || summary.beginner;
    if (summaryLowCount) {
      summaryPieData.push({
        color: '#d69f00',
        label: summaryLowCount.toString?.(),
        text: 'Beginner',
        value: summaryLowCount
      });
    }

    summaryPieData.forEach((x) => {
      x.tooltip = `
        <div>${x.text}</div>
        <div>${x.label}</div>
      `;
    });
  }

  const ariaLabelStudentSummaryWidget = (hasAssignments && summary) ? t('ariaLabelStudentSummaryWidget', {
    summaryHighCount: summaryHighCount || 0,
    summaryHighCountAssignments: summaryHighCount !== 1 ? t('assignments') : t('assignment'),
    summaryLowCount: summaryLowCount || 0,
    summaryLowCountAssignments: summaryLowCount !== 1 ? t('assignments') : t('assignment'),
    summaryMediumCount: summaryMediumCount || 0,
    summaryMediumCountAssignments: summaryMediumCount !== 1 ? t('assignments') : t('assignment')
  }) : t('ariaLabelStudentSummaryWidgetNoScoresYet');

  const noScoresYet = t('noScoresYet', 'No scores yet');
  // eslint-disable-next-line max-len
  const ariaLabelWeekGrades = ((hasAssignments && classroomProgressData?.weekGrades?.length) ? classroomProgressData.weekGrades.map((weekGrade) => {
    return t('ariaLabelWeekGrade', {
      averagePercent: (weekGrade?.averageScore >= 0 ? `${weekGrade.averageScore.toFixed()}%` : t('unknown')),
      end: weekGrade?.end || t('unknown'),
      start: weekGrade?.start || t('unknown'),
      submittedCount: weekGrade?.submittedCount >= 0 ? weekGrade.submittedCount : t('unknown'),
      submittedCountAssignments: weekGrade?.submittedCount !== 1 ? t('assignments') : t('assignment')
    });
  }) : [noScoresYet]).join(' ');

  const ariaLabelWeeklyPerformanceWidget = t('ariaLabelWeeklyPerformanceWidget', {
    ariaLabelWeekGrades
  });

  return (
    <Container className='progress-view progress-view-override' fluid>
      <CourseListBanner
        currentContentType={selectedContentType}
        hideAddStudentButton={true}
        history={history}
        isMyProgress={false}
        title={t('BannerTitle', 'No Progress View Banner Title')} />
      <div className='widget-wrapper'>
        {(userManager.loaded && classroomManager.loaded && course !== null) ? (
          <>
            <div className='left-col'>
              <div aria-label={ariaLabelStudentSummaryWidget}
                className='widget summary-widget student-summary-widget' tabIndex='0'>
                <div aria-hidden={true} className='w-title'>
                  {t('SummaryLabel', 'Missing summary label translation')}
                </div>
                {hasAssignments && summary ? (
                  <div aria-hidden={true} className='summary'>
                    <D3PieChart
                      data={summaryPieData}
                      fontSize={10}
                      height={116}
                      labelPlacement={0.3}
                      width={116} />
                    <Legend
                      data={summary} t={t} />
                  </div>
                ) : (
                  <div aria-hidden={true} className='no-scores'>
                    <p>{noScoresYet}</p>
                  </div>
                )}
              </div>
              <div aria-label={ariaLabelWeeklyPerformanceWidget || t('noScoresYet')}
                className='widget weekly-performance-widget' tabIndex='0'>
                <div aria-hidden={true} className='w-title'>
                  {t('WeeklyPerformanceLabel', 'Missing weekly performance label translation')}
                </div>
                {(hasAssignments && classroomProgressData?.weekGrades?.length) ? (
                  <div aria-hidden={true} className='weekly-performance-container'>
                    <WeeklyPerformanceChart data={classroomProgressData.weekGrades} t={t} width={320} />
                  </div>
                ) : (
                  <div aria-hidden={true} className='no-scores'>
                    <p>{noScoresYet}</p>
                  </div>
                )}
              </div>
            </div>
            <div className='right-col'>
              <KeyboardActionListener>
                <div className='widget assignments-widget' tabIndex={0}>
                  <div className='assignments-top-padding' />
                  <div className='assignments-widget-inner'>
                    <Assignments />
                  </div>
                </div>
              </KeyboardActionListener>
            </div>
          </>
        ) : null}
      </div>
      {renderAssignmentsPopup.render && (
        <StudentAssignmentsPopup
          key={renderAssignmentsPopup.key}
          activityInstanceId={renderAssignmentsPopup.activityInstanceId}
          courseElement={renderAssignmentsPopup.courseElement}
          hideSelf={hideAssignment}
          history={history} />
      )}
    </Container>
  );
});

export default ProgressViewOverride;

SatCoreRegister('ProgressView', ProgressViewOverride);
